import axios from 'axios';

/**
 * HTTP client for the NEW soil application group lifecycle (issue #417 Slice 4).
 * Offline queueing lives in soilGroupSyncService — this module is pure HTTP so
 * the drainer can replay exactly the same calls.
 */

const authHeaders = () => {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};
const base = '/api/v1/nutrition/soil-application-sessions';

export interface SoilMyGroup {
  id: string;
  sessionId: string;
  groupId: string;
  label: string;
  productName: string;
  month: string;
  season: string;
  orchardNames: string[];
  plannedKg: number;
  /** Frozen at plan time. Optional: rows cached before U5 shipped lack them. */
  cultivars?: string[];
  treeCount?: number;
  areaHa?: number;
  status: 'not_started' | 'in_progress' | 'complete' | 'signed_off';
  startedAt?: string | null;
}

export interface SoilAwaitingGroup {
  id: string;
  sessionId: string;
  groupId: string;
  label: string;
  productName: string;
  month: string;
  season: string;
  orchardNames: string[];
  plannedKg: number;
  completedAt: string | null;
  completedBy: string | null;
  ecReading: number | null;
  signatureName: string | null;
}

export interface SoilSignOffBulkResult {
  results: Array<{ sessionId: string; groupId: string; ok: boolean; error?: string }>;
  okCount: number;
  failCount: number;
}

export interface SoilGroupStartPayload { startedAt: string; startKey: string }

export interface SoilGroupCompletePayload {
  startedAt?: string;
  completedAt: string;
  ecReading?: number;
  signatureName?: string;
  captureChannel: 'field' | 'backcapture';
  completeKey: string;
}

export const soilGroupService = {
  async listMyGroups(): Promise<{ items: SoilMyGroup[]; hasLinkedOperator: boolean }> {
    return (await axios.get(`${base}/my`, { headers: authHeaders() })).data;
  },
  async listAwaitingSignoff(): Promise<SoilAwaitingGroup[]> {
    return (await axios.get(`${base}/awaiting-signoff`, { headers: authHeaders() })).data;
  },
  async startGroup(sessionId: string, groupId: string, payload: SoilGroupStartPayload) {
    return (await axios.post(`${base}/${sessionId}/groups/${groupId}/start`, payload, { headers: authHeaders() })).data;
  },
  async completeGroup(sessionId: string, groupId: string, payload: SoilGroupCompletePayload) {
    return (await axios.post(`${base}/${sessionId}/groups/${groupId}/complete`, payload, { headers: authHeaders() })).data;
  },
  async signOffGroup(sessionId: string, groupId: string) {
    return (await axios.post(`${base}/${sessionId}/groups/${groupId}/signoff`, {}, { headers: authHeaders() })).data;
  },
  async signOffBulk(items: Array<{ sessionId: string; groupId: string }>): Promise<SoilSignOffBulkResult> {
    // Deliberately NOT `${base}/...`: base is the soil-application-sessions
    // resource, but this bulk endpoint is mounted under /soil-groups instead
    // (Task 1, decision 2) to sidestep a documented `:id`-capture hazard on
    // the sessions router. Do not "fix" this back onto base.
    return (await axios.post('/api/v1/nutrition/soil-groups/sign-off-bulk', { items }, { headers: authHeaders() })).data;
  },
};
